/**
 * Basket API utilities
 * Contains methods for interacting with the basket service:
 * - addSkuToBasket: Add a product SKU to the basket
 * - getBasket: Retrieve the current basket content
 * - addServiceToBasket: Add a service item (e.g. Protect Plus) to a basket item
 * - removeServiceFromBasket: Remove a service item from the basket
 * - changeItemQuantity: Update the quantity of a basket item
 */
import {
  APIError,
  APIErrorCode,
  AddToBasketRequest,
  AddToBasketResponse,
  BasketRequestResponse,
} from "./types";

export const BASKET_CLIENT_ID = "web_pdp_scaffold_ui";
const APPLICATION_JSON = "application/json";

const getAuth0Token = async (): Promise<string | null> => {
  const auth0 = window.__auth0__ || window.auth0;
  return auth0?.getTokenIfAuthenticated() ?? null;
};

// ---- SKU operations ----

interface AddSkuToBasketParams extends AddToBasketRequest {
  basketApiBaseUrl: string;
}

export const addSkuToBasket = async (
  params: AddSkuToBasketParams,
): Promise<AddToBasketResponse> => {
  const { url, options } = await requestBuilder(params);

  let response;
  let errorMessage: string | undefined;

  try {
    response = await fetch(url, options);
  } catch (err) {
    errorMessage = err instanceof Error ? err.message : String(err);
  }

  if (!response?.ok && response?.status !== 422) {
    return {
      errors: [
        {
          code: APIErrorCode.DEFAULT,
          message:
            "Sorry, we've had a technical problem. Please refresh your page and try again.",
        },
      ],
      commerceItems: [],
      errorMessage,
      success: false,
    };
  }

  const responseData: BasketRequestResponse = await response.json();
  const { basket: { orderId = "", commerceItems = [] } = {} } = responseData;
  const errors = parseBFFBasketErrors(
    responseData,
    params.isShipFromStore ?? false,
  );

  return {
    ...responseData,
    errors,
    commerceItems,
    orderId,
    success: response.ok,
    responseStatus: response.status,
  };
};

const requestBuilder = async (params: AddSkuToBasketParams) => {
  const {
    quantity,
    skuId,
    variantId,
    childItems,
    express,
    basketApiBaseUrl,
    isShipFromStore,
  } = params;

  const body = childItems
    ? { quantity, skuId, variantId, childItems }
    : { quantity, skuId, variantId };

  let url = `${basketApiBaseUrl}/items/${variantId}`;
  if (express) {
    url = `${basketApiBaseUrl}/items/express/${variantId}${isShipFromStore ? "?sourced-by=STORE" : ""}`;
  }

  const token = await getAuth0Token();

  const options: RequestInit = {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": APPLICATION_JSON,
      "x-api-client-id": BASKET_CLIENT_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  return { url, options };
};

export const getBasket = async (
  basketApiBaseUrl: string,
): Promise<BasketRequestResponse> => {
  const token = await getAuth0Token();

  const options: RequestInit = {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": APPLICATION_JSON,
      "x-api-client-id": BASKET_CLIENT_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  try {
    const response = await fetch(basketApiBaseUrl, options);

    if (!response.ok) {
      return {
        errors: [
          {
            code: APIErrorCode.DEFAULT,
            message: "An error occurred while fetching basket",
          },
        ],
      };
    }

    return await response.json();
  } catch (err) {
    return {
      errors: [
        {
          code: APIErrorCode.DEFAULT,
          message: err instanceof Error ? err.message : String(err),
        },
      ],
    };
  }
};

const parseBFFBasketErrors = (
  json: BasketRequestResponse,
  isShipFromStore: boolean,
): APIError[] => {
  const { errors, basket: { orderId } = {} } = json;

  if (errors.length > 0) {
    errors.forEach((error: APIError) => {
      if (error.message === "Sorry, this is out of stock online.") {
        if (isShipFromStore) {
          error.message = "Sorry, this item is out of stock";
        } else {
          error.message = "Sorry, this item is out of stock online";
        }
      }
    });
    return errors;
  }

  if (!orderId) {
    return [
      {
        code: APIErrorCode.DEFAULT,
        message: "order-id missing from returned basket",
      },
    ];
  }

  return [];
};

// ---- Basket service item operations (PP+, delivery services, etc.) ----

type ServiceApiResponse = {
  success: boolean;
  commerceItems: Array<object>;
};

const serviceApiFailure = (): ServiceApiResponse => ({
  success: false,
  commerceItems: [],
});

export const addServiceToBasket = async (
  baseUrl: string,
  parentItemId: string,
  serviceId: string,
  quantity = 1,
): Promise<ServiceApiResponse> => {
  try {
    const response = await fetch(`${baseUrl}/items/service`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        parentItemId,
        serviceId,
        quantity,
        bundleComponentVariantId: null,
      }),
      headers: {
        "Content-Type": APPLICATION_JSON,
        "x-api-client-id": BASKET_CLIENT_ID,
      },
    });

    if (!response.ok) return serviceApiFailure();

    const {
      basket: { commerceItems },
    } = await response.json();
    return { success: true, commerceItems };
  } catch {
    return serviceApiFailure();
  }
};

export const removeServiceFromBasket = async (
  baseUrl: string,
  serviceItemId: string,
): Promise<ServiceApiResponse> => {
  try {
    const response = await fetch(`${baseUrl}/items/${serviceItemId}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "x-api-client-id": BASKET_CLIENT_ID },
    });

    if (!response.ok) return serviceApiFailure();

    const {
      basket: { commerceItems },
    } = await response.json();
    return { success: true, commerceItems };
  } catch {
    return serviceApiFailure();
  }
};

export const changeItemQuantity = async (
  baseUrl: string,
  serviceItemId: string,
  quantity: number,
): Promise<ServiceApiResponse> => {
  try {
    const response = await fetch(`${baseUrl}/items`, {
      method: "PUT",
      credentials: "include",
      body: JSON.stringify({
        items: [{ basketItemId: serviceItemId, quantity }],
      }),
      headers: {
        "Content-Type": APPLICATION_JSON,
        "x-api-client-id": BASKET_CLIENT_ID,
      },
    });

    if (!response.ok) return serviceApiFailure();

    const {
      basket: { commerceItems },
    } = await response.json();
    return { success: true, commerceItems };
  } catch {
    return serviceApiFailure();
  }
};
